import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { MDXProvider } from '@mdx-js/react'
import { tint } from 'polished'
import {
  Container,
  Row,
  RowColumn,
  Section,
  List,
} from 'chaoskit/src/components'
import { tealBrightTeal } from 'chaoskit/src/assets/styles/utility/gradient'
import { text } from 'chaoskit/src/assets/styles/utility'

import BaseLayout from './BaseLayout'

import StaticFooter from '~partials/footer/StaticFooter'
import Hero, {
  HeroContent,
  HeroFormWrapper,
  HeroTitleSub,
  HeroTitle,
  HeroFormTitle,
} from '~partials/Hero'
import TeamIntro from '~partials/home/TeamIntro'
import Data from '~components/Data'
import FooterCTA from '~partials/footer/FooterCTA'
import ProductOverview from '~partials/product/ProductOverview'
import HelmetSEO from '~partials/global/HelmetSEO'
import Quote from '~components/Quote'
import { listBullet } from '~styles/utility'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import track from '~helpers/track'
import MarketoForm from '~components/MarketoForm'
import MutinyTag from '~components/MutintyTag'

const WhitepaperLandingPageLayout = ({
  article: {
    frontmatter: {
      seoTitle,
      description,
      socialMediaImage,
      hero,
      leftColumnContent,
      rightColumnContent,
      showProductOverview,
      showFooterCTA,
      hideTeamIntro,
    },
  },
  topBlock,
  nodeType,
}) => {
  const theme = useTheme()

  return (
    <BaseLayout noFooter headerProps={{ sticky: false, showNav: false }}>
      <HelmetSEO
        title={seoTitle}
        description={description}
        image={socialMediaImage}
        noIndex
      />
      <MutinyTag />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero contrast={false} css={tealBrightTeal(theme)}>
        <HeroContent>
          <Container>
            <Row gutter={{ base: 'base', medium: 'xlarge' }}>
              <RowColumn
                size={{ medium: 6 }}
                css={{
                  textAlign: 'center',

                  [theme.mq.medium]: {
                    textAlign: 'left',
                  },
                }}
                className="u-contrast"
              >
                <div
                  css={{
                    [theme.mq.medium]: {
                      position: 'sticky',
                      top: theme.space.base,
                    },
                  }}
                >
                  {hero.headingSub && (
                    <h4
                      css={[
                        theme.fontSize.xlarge__fluid,
                        {
                          marginBottom: theme.space.base,
                          color: `${theme.contrast.muted} !important`,
                        },
                      ]}
                    >
                      {hero.headingSub}
                    </h4>
                  )}
                  <HeroTitle as="h2" css={{ marginTop: 0 }}>
                    <Data>{hero.heading}</Data>
                  </HeroTitle>
                  <HeroTitleSub>
                    <Data markdown>{hero.content}</Data>
                  </HeroTitleSub>
                </div>
              </RowColumn>
              <RowColumn size={{ medium: 6 }}>
                <HeroFormWrapper>
                  <HeroFormTitle>{hero.form.heading}</HeroFormTitle>
                  <MarketoForm
                    id={hero.form.embed}
                    fieldValues={{ programID: hero.form.programId }}
                    thankYouMessage={hero.form.thankYouMessage}
                    onSuccess={() => {
                      track('Content downloaded', {
                        label: `marketo-${hero.form.embed}`,
                      })
                    }}
                  />
                </HeroFormWrapper>
              </RowColumn>
            </Row>
          </Container>
        </HeroContent>
      </Hero>
      {topBlock && topBlock}
      <Section
        css={{
          [theme.mq.small__max]: {
            padding: '0 !important',
          },

          [theme.mq.medium]: {
            background: `linear-gradient(to right, ${
              theme.color.panel.base
            } 50%, ${tint(0.15, theme.color.dark.base)} 50%, ${
              theme.color.dark.base
            })`,
          },

          'h2, h3, h4': [
            theme.fontSize.xlarge__fluid,
            {
              fontWeight: theme.fontWeight.medium,
              marginBottom: theme.space.large,
            },
          ],
        }}
      >
        <Container
          css={{
            [theme.mq.small__max]: {
              paddingLeft: 0,
              paddingRight: 0,
            },
          }}
        >
          <Row gutter={{ base: 'collapse', medium: 'xlarge' }}>
            <RowColumn size={{ medium: 6 }}>
              <div
                css={{
                  [theme.mq.small__max]: {
                    padding: theme.space.base,
                    background: theme.color.panel.base,
                  },
                }}
              >
                <MDXProvider
                  components={{
                    ul: (props) => (
                      <List space="base" css={[listBullet(theme)]} {...props} />
                    ),
                  }}
                >
                  <Data markdown>{leftColumnContent}</Data>
                </MDXProvider>
              </div>
            </RowColumn>
            <RowColumn
              className="u-contrast"
              css={{
                'h2, h3, h4': {
                  color: `${theme.color.primary.base} !important`,
                },
              }}
              size={{ medium: 6 }}
            >
              <div
                css={{
                  'a:not([class]), a[class=""]': [text.underline],

                  [theme.mq.small__max]: {
                    padding: theme.space.base,
                    background: `linear-gradient(to bottom, ${tint(
                      0.15,
                      theme.color.dark.base
                    )}, ${theme.color.dark.base})`,
                  },
                }}
              >
                <MDXProvider
                  components={{
                    blockquote: (props) => <Quote contrastQuotes {...props} />,
                  }}
                >
                  <Data markdown>{rightColumnContent}</Data>
                </MDXProvider>
              </div>
            </RowColumn>
          </Row>
        </Container>
      </Section>
      {!hideTeamIntro && (
        <TeamIntro
          cta={{
            label: hero.form.heading,
            url: '#form',
          }}
        />
      )}
      {showProductOverview && (
        <Container>
          <ProductOverview />
        </Container>
      )}
      {showFooterCTA && <FooterCTA />}
      <StaticFooter />
    </BaseLayout>
  )
}

WhitepaperLandingPageLayout.propTypes = {
  article: PropTypes.object.isRequired,
  topBlock: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  nodeType: PropTypes.string.isRequired,
}

export default WhitepaperLandingPageLayout
